/**
 * The occasion calendar — one entry point over four very different classes.
 *
 * ⚠ ADMIN-IMPORT-ONLY BY CONSTRUCTION. The extension must never reach this
 * module: it is a multi-year table for a dozen calendars, and the entire point
 * of baking windows in the admin is that none of it reaches the shopper.
 * `extensions/rollup-plugin-no-calendar.js` fails the storefront build on it.
 *
 * `occasionDate` NEVER extrapolates. Past the horizon it returns null and the
 * admin says the occasion needs updating. A wrong Diwali is worse than a missing
 * one — Islamic dates vary by ±1 day by country, and the panchang tables are
 * human-verified to a horizon and not one year beyond it.
 */

import type { CivilDate } from '../../shared/config.ts';

import { algorithmicDate } from './algorithmic.ts';
import { GENERATED, GENERATED_FROM_YEAR, GENERATED_TO_YEAR } from './generated.ts';
import type { Hemisphere } from './registry.ts';
import { hemisphereOf, OCCASIONS, OCCASIONS_BY_ID, SEASON_MONTHS } from './registry.ts';
import { TABLE_END_YEAR, tableDate, TABLES } from './tables.ts';

export { marchEquinoxJDE, nowruz } from './astro.ts';
export { GENERATED_FROM_YEAR, GENERATED_TO_YEAR } from './generated.ts';
export * from './registry.ts';
export { checkCalendarVerified, TABLES } from './tables.ts';
export { TABLE_END_YEAR } from './tables.ts';

/** The last year ANY class can answer for. */
export const HORIZON_YEAR = Math.min(GENERATED_TO_YEAR, TABLE_END_YEAR);

export interface OccasionResolution {
  date: CivilDate;
  /** Every occurrence in that Gregorian year. Usually one; two for a drifting Hijri feast. */
  all: CivilDate[];
  precision: 'exact' | 'approx_1d';
}

/**
 * Resolve an occasion to a Gregorian civil date, or null.
 *
 * `hemisphere` only affects the four seasonal ids. Passing it for anything else
 * is harmless and ignored — Christmas is December in Sydney too.
 */
export function resolveOccasion(
  id: string,
  y: number,
  variant?: string,
  hemisphere: Hemisphere = 'north'
): OccasionResolution | null {
  const meta = OCCASIONS_BY_ID[id];
  if (!meta) return null;

  // ── seasonal, hemisphere-flipped ──────────────────────────────────────────
  const season = SEASON_MONTHS[hemisphere][id];
  if (season) {
    const date = { y, m: season[0], d: season[1] };
    return { date, all: [date], precision: 'exact' };
  }

  // ── Class F and A: correct forever, no horizon ────────────────────────────
  if (meta.cls === 'F' || meta.cls === 'A') {
    // A variant is a DIFFERENT RULE, not a modifier: Father's Day in Australia
    // is the first Sunday of September, not a shifted third-Sunday-of-June.
    const date = algorithmicDate(variant ? `${id}-${variant}` : id, y) ?? algorithmicDate(id, y);
    return date ? { date, all: [date], precision: meta.precision } : null;
  }

  // ── Class G: the frozen ICU table ─────────────────────────────────────────
  if (meta.cls === 'G') {
    if (y < GENERATED_FROM_YEAR || y > GENERATED_TO_YEAR) return null;
    const hits = GENERATED[id]?.[y];
    if (!hits?.length) return null;
    const all = hits.map(([m, d]) => ({ y, m, d }));
    return { date: all[0], all, precision: meta.precision };
  }

  // ── Class T: the human-verified table ─────────────────────────────────────
  if (y > TABLE_END_YEAR) return null;
  const row = tableDate(id, y, variant);
  if (!row) return null;
  const date = { y, m: row[0], d: row[1] };
  return { date, all: [date], precision: meta.precision };
}

/**
 * The callback shape `bakeWindows` expects.
 *
 * It is injected rather than imported by the scheduler, which is what keeps the
 * calendar out of anything that does not explicitly ask for it.
 */
export function makeOccasionDates(hemisphere: Hemisphere = 'north') {
  return (id: string, y: number, variant?: string): CivilDate | null =>
    resolveOccasion(id, y, variant, hemisphere)?.date ?? null;
}

/** The default resolver, for callers with no store country in hand. */
export const occasionDates = makeOccasionDates('north');

/** Convenience for the admin picker: is this occasion answerable for this year? */
export function occasionAvailable(id: string, y: number, variant?: string): boolean {
  return resolveOccasion(id, y, variant) !== null;
}

export { hemisphereOf };

// ─────────────────────────────────────────────────────────────────────────────
// The load-time assertion
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every registered occasion must resolve for the current year.
 *
 * An occasion in the registry with no rule and no table row is an entry that
 * appears in the merchant's picker, saves cleanly, bakes to zero windows, and
 * then never runs — with a green build and no error anywhere. That is the same
 * shape of failure as a catalogued effect with a missing sprite, and it gets the
 * same treatment: fail loudly, at load, in development.
 */
export function assertRegistryResolves(year = new Date().getUTCFullYear()): string[] {
  const broken: string[] = [];
  for (const o of OCCASIONS) {
    // Class G and T cannot answer outside their own horizons; that is correct
    // behaviour, not a broken entry.
    //
    // ⚠ Class T horizons are PER OCCASION, not global. Loy Krathong is verified
    // only to 2030 while the Hindu tables reach 2033, and a single
    // TABLE_END_YEAR would both flag Loy Krathong as broken in 2031 AND skip the
    // Hindu occasions entirely in 2034 — hiding a real gap behind a false one.
    if (o.cls === 'G' && (year < GENERATED_FROM_YEAR || year > GENERATED_TO_YEAR)) continue;
    if (o.cls === 'T' && year > (TABLES[o.id]?.verifiedThrough ?? 0)) continue;
    if (!resolveOccasion(o.id, year)) broken.push(o.id);
    for (const v of o.variants ?? []) {
      if (!resolveOccasion(o.id, year, v.id)) broken.push(`${o.id}@${v.id}`);
    }
  }
  return broken;
}
